'use client';

import type { KeenSliderInstance } from 'keen-slider';
import { useKeenSlider } from 'keen-slider/react';
import Image from 'next/image';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import LinkButtonPrimary from '../_lib/ui/LinkButtonPrimary';
import LinkButtonSecondary from '../_lib/ui/LinkButtonSecondary';

import 'keen-slider/keen-slider.min.css';

// --- Interfaces ---
interface ImageType {
  id: number;
  src: string;
  caption: string;
  heading?: string;
  subheading?: string;
}

interface CarouselProps {
  images?: ImageType[];
  slides?: ImageType[];
  isGallery?: boolean;
  prioritizeFirstImage?: boolean;
  onSliderReady?: (slider: KeenSliderInstance) => void;
}

// Custom hook to detect prefers-reduced-motion
const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check if window is available (client-side)
    if (typeof window !== 'undefined') {
      // Initial check
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);

      // Add listener for changes
      const handleChange = (e: MediaQueryListEvent) => {
        setPrefersReducedMotion(e.matches);
      };

      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      }
    }
  }, []);

  return prefersReducedMotion;
};

// --- Carousel Component ---
const Carousel: React.FC<CarouselProps> = ({
  images,
  slides,
  isGallery,
  prioritizeFirstImage = true,
  onSliderReady,
}) => {
  const [isPaused, setIsPaused] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Configure autoplay options
  const autoplayDelay = prefersReducedMotion ? 8000 : 6000;

  // Setup keen-slider with autoplay
  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      loop: true,
      slides: { perView: 1 },
      drag: true,
      created(s) {
        setCurrentSlide(s.track.details.rel);
        onSliderReady?.(s);
      },
      slideChanged(s) {
        setCurrentSlide(s.track.details.rel);
      },
    },
    [
      // Autoplay plugin
      (slider) => {
        let timeout: ReturnType<typeof setTimeout>;
        let mouseOver = false;

        function clearNextTimeout() {
          clearTimeout(timeout);
        }

        function nextTimeout() {
          clearTimeout(timeout);
          if (mouseOver || isPaused) return;
          timeout = setTimeout(() => {
            slider.next();
          }, autoplayDelay);
        }

        slider.on('created', () => {
          slider.container.addEventListener('mouseover', () => {
            mouseOver = true;
            clearNextTimeout();
          });
          slider.container.addEventListener('mouseout', () => {
            mouseOver = false;
            nextTimeout();
          });
          nextTimeout();
        });

        slider.on('dragStarted', clearNextTimeout);
        slider.on('animationEnded', nextTimeout);
        slider.on('updated', nextTimeout);

        return () => {
          clearNextTimeout();
        };
      },
    ],
  );

  // Memoize type guards to avoid recreating them on each render
  const isImageType = useCallback((item: unknown): item is ImageType => {
    return (
      typeof item === 'object' &&
      item !== null &&
      'src' in item &&
      'caption' in item &&
      typeof (item as ImageType).src === 'string' &&
      typeof (item as ImageType).caption === 'string'
    );
  }, []);

  const isSlideType = useCallback((item: unknown): item is ImageType => {
    return (
      typeof item === 'object' &&
      item !== null &&
      'src' in item &&
      'caption' in item &&
      'heading' in item &&
      'subheading' in item &&
      typeof (item as ImageType).src === 'string' &&
      typeof (item as ImageType).caption === 'string' &&
      typeof (item as ImageType).heading === 'string' &&
      typeof (item as ImageType).subheading === 'string'
    );
  }, []);

  // Memoize items to prevent unnecessary re-renders
  const items = useMemo(() => (isGallery ? images || [] : slides || []), [isGallery, images, slides]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        instanceRef.current?.prev();
      } else if (e.key === 'ArrowRight') {
        instanceRef.current?.next();
      } else if (e.key === ' ' || e.key === 'Spacebar') {
        setIsPaused((prev) => !prev);
        e.preventDefault();
      }
    },
    [instanceRef],
  );

  // Add passive event listeners to improve scrolling performance
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown, { passive: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <section
      className={`relative overflow-hidden ${isGallery ? 'h-[60vh]' : 'h-[90vh]'}`}
      id={isGallery ? 'photo-slideshow' : 'hero-slideshow'}
      aria-label={isGallery ? 'Photo gallery slideshow' : 'Featured content slideshow'}
      aria-roledescription='carousel'
    >
      {/* Pause/Play Button */}
      <div className='absolute top-4 right-4 z-20'>
        <button
          type='button'
          onClick={() => setIsPaused(!isPaused)}
          className='p-2 rounded-full bg-white/20 dark:bg-gray-800/40 backdrop-blur-sm text-white hover:bg-white/40 dark:hover:bg-gray-800/60 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-white'
          aria-label={isPaused ? 'Play slideshow' : 'Pause slideshow'}
        >
          {isPaused ? (
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z'
              />
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          ) : (
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          )}
        </button>
      </div>

      {/* Keen Slider Container */}
      <div ref={sliderRef} className='keen-slider h-full w-full flex flex-nowrap'>
        {items.map((item, index) => {
          const imageSrc = item.src || '';
          const imageAlt = item.caption || '';
          const isFirstSlide = index === 0;
          const isActive = currentSlide === index;

          return (
            <div
              className='keen-slider__slide relative h-full w-full shrink-0'
              key={item.id}
              role='group'
              aria-roledescription='slide'
              aria-label={`Slide ${index + 1} of ${items.length}`}
            >
              <Image
                src={imageSrc}
                alt={imageAlt}
                quality={isFirstSlide ? 90 : 75} // Keep high quality only for first slide
                priority={isFirstSlide && prioritizeFirstImage} // Only prioritize first slide
                fill
                className='object-cover'
                sizes='(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 70vw'
                loading={isFirstSlide ? 'eager' : 'lazy'}
                decoding={isFirstSlide ? 'sync' : 'async'}
                placeholder='blur'
                blurDataURL={imageSrc.replace(/(\.[^.]+)$/, '-blur$1')}
              />
              <div className='absolute inset-0 bg-black/40' />
              <div className='absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-10'>
                <div
                  className={`max-w-4xl mx-auto transition-all duration-800 ease-out ${
                    isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
                  }`}
                  style={{
                    transitionDelay: prefersReducedMotion ? '0s' : '0.2s',
                    transitionDuration: prefersReducedMotion ? '0.1s' : '0.8s',
                  }}
                >
                  {!isGallery && isSlideType(item) && (
                    <>
                      <h2 className='text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg px-12 md:px-0 leading-8 md:leading-14 transition-all duration-150 ease-in-out'>
                        {item.heading}
                      </h2>
                      <p className='text-xl md:text-2xl text-white/90 drop-shadow-md px-12 md:px-0 leading-5 md:leading-6 transition-all duration-150 ease-in-out'>
                        {item.subheading}
                      </p>
                      <div className='flex flex-col md:flex-row items-center justify-center mt-8 gap-x-4 gap-y-3'>
                        <LinkButtonPrimary href='#contact-us' aria-label='Contact us to learn more' className='group'>
                          Contact Us
                          <svg
                            className={`w-5 h-5 ml-2 ${prefersReducedMotion ? '' : 'group-hover:translate-x-1 transition-transform'}`}
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                            aria-hidden={true}
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M14 5l7 7m0 0l-7 7m7-7H3'
                            />
                          </svg>
                        </LinkButtonPrimary>
                        <LinkButtonSecondary href='#about' aria-label='Learn more about our services'>
                          Learn More
                        </LinkButtonSecondary>
                      </div>
                    </>
                  )}
                  {isGallery && isImageType(item) && (
                    <div className='bg-black/50 backdrop-blur-sm px-4 py-2 rounded-lg'>
                      <p className='text-white text-lg'>{item.caption}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Prev/Next Buttons */}
      <div className='absolute inset-y-0 left-4 z-10 flex items-center'>
        <button
          type='button'
          onClick={() => instanceRef.current?.prev()}
          className='p-2 rounded-full bg-white/20 dark:bg-gray-800/40 backdrop-blur-sm text-white hover:bg-white/40 dark:hover:bg-gray-800/60 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-white'
          aria-label='Previous slide'
        >
          <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
          </svg>
        </button>
      </div>
      <div className='absolute inset-y-0 right-4 z-10 flex items-center'>
        <button
          type='button'
          onClick={() => instanceRef.current?.next()}
          className='p-2 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/40 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-white'
          aria-label='Next slide'
        >
          <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
          </svg>
        </button>
      </div>

      {/* Dot Navigation */}
      {instanceRef.current && (
        <div className='absolute bottom-8 left-0 right-0 flex justify-center space-x-5 z-10'>
          {[...Array(items.length)].map((_, index) => (
            <button
              type='button'
              key={index}
              onClick={() => instanceRef.current?.moveToIdx(index)}
              aria-label={`Go to slide ${index + 1}`}
              className={`w-5 h-5 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white ${
                index === currentSlide ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      )}

      {/* Bottom Gradient */}
      <div className='absolute bottom-0 left-0 right-0 h-24 bg-linear-to-t from-white dark:from-gray-900 to-transparent pointer-events-none z-10' />

      {/* Marker div for header intersection observer */}
      <div id='carousel-bottom-marker' className='absolute bottom-0' />
    </section>
  );
};

export default Carousel;
